<template>
    <v-dialog
        :value="value"
        max-width="560px"
        @input="$emit('input', $event)"
    >
        <v-card v-if="value" class="social-publish-card">
            <v-card-title>
                <span class="text-h5">Публикация в {{ networkLabel }}</span>
            </v-card-title>

            <v-card-text>
                <template v-if="alreadyPublished && !forceRepublish">
                    <p>
                        Уже опубликовано{{ status.postedAt ? ' ' + formatDate(status.postedAt) : '' }}.
                    </p>
                    <p v-if="!status.externalPostUrl">
                        Прямая ссылка на пост недоступна для этой сети.
                    </p>
                </template>

                <template v-else>
                    <v-img
                        v-if="imageUrl"
                        :src="imageUrl"
                        max-height="220"
                        contain
                        class="mb-3 grey lighten-3"
                    />
                    <p v-else class="text--secondary">
                        Пост уйдёт без картинки - у новости нет картинки анонса.
                    </p>

                    <v-textarea
                        v-model="text"
                        label="Текст поста"
                        hint="Первый абзац станет жирным заголовком, остальное - обычный текст"
                        persistent-hint
                        :counter="textLimit"
                        :maxlength="textLimit"
                        auto-grow
                        outlined
                        :disabled="loading"
                    />
                    <p class="text--secondary text-caption mt-2 mb-0">
                        {{ footerHint }}
                    </p>
                </template>

                <v-alert v-if="error" type="error" dense class="mt-2">
                    {{ error }}
                </v-alert>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>

                <template v-if="alreadyPublished && !forceRepublish">
                    <v-btn text @click="close">Закрыть</v-btn>
                    <v-btn
                        v-if="status.externalPostUrl"
                        color="blue darken-1"
                        text
                        :href="status.externalPostUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Открыть пост
                    </v-btn>
                    <v-btn color="red darken-1" text @click="forceRepublish = true">
                        Опубликовать ещё раз
                    </v-btn>
                </template>

                <template v-else>
                    <v-btn text :disabled="loading" @click="close">Отмена</v-btn>
                    <v-btn
                        color="blue darken-1"
                        text
                        :loading="loading"
                        :disabled="!text.trim()"
                        @click="doPublish"
                    >
                        Опубликовать
                    </v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'SocialPublishDialog',
    props: {
        value: { type: Boolean, default: false },
        network: { type: String, required: true },
        networkLabel: { type: String, required: true },
        newsId: { type: [Number, String], required: true },
        initialText: { type: String, default: '' },
        articleUrl: { type: String, default: '' },
        imageUrl: { type: String, default: '' },
        textLimit: { type: Number, default: 4000 },
        status: { type: Object, default: null }
    },
    data() {
        return {
            text: this.initialText,
            loading: false,
            error: '',
            forceRepublish: false
        };
    },
    computed: {
        alreadyPublished() {
            return Boolean(this.status && this.status.status === 'success');
        },
        footerHint() {
            // В ВК и ОК бот дописывает единую подпись с каналами редакции
            // (api: EditorialSignature), в Telegram и MAX - строку со ссылками
            // «Подробности | Подписаться».
            return ['vk', 'ok'].includes(this.network)
                ? 'В конце поста бот сам добавит подпись с каналами редакции (сайт, Telegram, MAX, ВК).'
                : 'В конце поста бот сам добавит строку «Подробности | Подписаться» со ссылками на новость и канал.';
        }
    },
    watch: {
        value(open) {
            if (open) {
                this.text = this.initialText;
                this.error = '';
                this.forceRepublish = false;
            }
        }
    },
    methods: {
        close() {
            this.$emit('input', false);
        },
        formatDate(iso) {
            return new Date(iso).toLocaleString(['ru-RU'], {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        async doPublish() {
            this.loading = true;
            this.error = '';
            try {
                const result = await this.$api(`/admin/news/publish/${this.newsId}/${this.network}`, {
                    method: 'POST',
                    body: { text: this.text, url: this.articleUrl }
                });
                if (result.status === 'error') {
                    this.error = result.error || 'Не удалось опубликовать';
                    this.$emit('published', result);
                    return;
                }
                this.$emit('published', result);
                this.close();
            } catch (e) {
                this.error = e.message || 'Не удалось опубликовать';
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
/*
 * App.vue глобально отключает overflow-y у .v-dialog (overflow-y: visible !important),
 * из-за чего на мобильных экранах карточка, не влезающая по высоте (длинный текст поста +
 * колоночные кнопки в мобильной раскладке), просто вылезает за нижний край экрана вместе
 * с кнопкой "Опубликовать" - её нельзя ни увидеть, ни проскроллить. Глобальное правило не
 * трогаем (не понятно, для чего оно заведено и что ещё на него завязано), вместо этого даём
 * скролл самой карточке - у неё свой overflow, не связанный с overflow родителя.
 */
.social-publish-card {
    max-height: 90vh;
    overflow-y: auto;
}

/*
 * ...а кнопки в подвале карточки закрепляем снизу: при длинном тексте поста (в ВК/ОК
 * уходит вся новость) textarea с auto-grow распирает карточку на несколько экранов,
 * и без sticky кнопка "Опубликовать" оказывается недостижимой на мобильных - до неё
 * нужно прокручивать весь текст.
 */
.social-publish-card .v-card__actions {
    position: sticky;
    bottom: 0;
    background-color: #fff;
    border-top: 1px solid #e2e8f0;
    z-index: 2;
}
</style>
